import type { EventType, EventUserStatus } from '@graphql/generated-base';
import type { EventMembership } from '@shared/components/AuthProvider';
import { useAuth, useEventMemberships } from '@shared/components/AuthProvider';
import { useSegmentGroupCallForEvent } from '@shared/core';
import { useCallback, useEffect, useMemo } from 'react';
import { useIntlRestrictedEvents } from '../../hooks/useIntlRestrictedEvents';

const NO_EVENTS: ReadonlySet<string> = new Set();

export type EventPickerProps = Readonly<
  {
    currentEventId?: string;
    onCreateNewEvent: (createMethod: 'event picker dialog') => void;
  } & UseEventPickerControllerReturn
>;
export type UseEventPickerControllerArgs = Readonly<{
  /** @default 'all' */
  membershipStatus?: EventUserStatus | 'adminOrOwner' | 'all';
  onEventClick: (args: { eventId: string; website: string; role: EventUserStatus; membershipsCount: number }) => void;
  onNoEventMembershipsFound?: () => void;
  /**
   * Whether events on the lean international experience may be selected. They can host cash funds
   * and external registry links but no Joy store item, so the answer depends on what the caller is
   * about to do with the selection — it is the caller's policy, not the picker's. Required rather
   * than defaulted so a new consumer has to make the call consciously.
   */
  blockIntlRestrictedEvents: boolean;
}>;

export type UseEventPickerControllerReturn = ReturnType<typeof useEventPickerController>;

export const useEventPickerController = (props: UseEventPickerControllerArgs) => {
  const { onEventClick, membershipStatus = 'adminOrOwner', onNoEventMembershipsFound, blockIntlRestrictedEvents } = props;
  const { executeSegmentGroupCallForEvent } = useSegmentGroupCallForEvent();

  const { hasInitializedOnce } = useAuth();
  const { eventMemberships: events } = useEventMemberships(undefined, { includeRegistryEligibility: true });
  const noMemberShipEventsFound = hasInitializedOnce && !events.length;

  useEffect(() => {
    if (noMemberShipEventsFound) {
      onNoEventMembershipsFound?.();
    }
  }, [hasInitializedOnce, noMemberShipEventsFound, onNoEventMembershipsFound]);

  const filterEvents = useCallback(
    (filterBy: UseEventPickerControllerArgs['membershipStatus']) => {
      const getFilterCondition = (filterBy: UseEventPickerControllerArgs['membershipStatus'], status: EventMembership['status']) => {
        if (filterBy === 'adminOrOwner') {
          return status === 'admin' || status === 'owner';
        } else if (filterBy === 'all' || !filterBy) {
          return true;
        } else {
          return filterBy === status;
        }
      };

      return events.filter(event => getFilterCondition(filterBy, event?.status));
    },
    [events],
  );

  const eventMemberships = useMemo(() => filterEvents(membershipStatus), [filterEvents, membershipStatus]);

  const { intlRestrictedEventIds } = useIntlRestrictedEvents(eventMemberships);

  // Kept apart on purpose: every international event is tagged as such, but only an unselectable one
  // is dimmed and inert. The two coincide for a store add and diverge for a cash fund add.
  const unselectableEventIds = blockIntlRestrictedEvents ? intlRestrictedEventIds : NO_EVENTS;

  const membershipsCount = eventMemberships.length;
  const handleOnEventClick = useCallback(
    (eventId: string, firebaseId: Maybe<string>, website: string, role: EventUserStatus, eventType: Maybe<EventType>) => {
      if (unselectableEventIds.has(eventId)) {
        return;
      }
      executeSegmentGroupCallForEvent(firebaseId, {
        postgresEventId: eventId,
        eventType: eventType,
      });
      onEventClick({ eventId, website, role, membershipsCount });
    },
    [onEventClick, membershipsCount, executeSegmentGroupCallForEvent, unselectableEventIds],
  );

  return {
    handleOnEventClick,
    eventMemberships,
    intlRestrictedEventIds,
    unselectableEventIds,
  } as const;
};
